import { ASSESSMENT_VARIANTS } from "core/consts";
import type {
  ICDAlgoliaHit,
  InsuranceAlgoliaHit,
  Solutions,
  ZodValidationIssue,
} from "core/types";
import Translations from "translations/types";
import { ZodError, ZodIssue, z, type ZodTypeAny } from "zod";
import { ICDAlgoliaOption, InsuranceAlgoliaOption } from "./shared";

/**
 * Attaches a validator that returns ZodValidationIssue[] to a base schema via superRefine.
 * Use when validation logic is expressed as (data) => issues[] rather than (data, ctx) => ctx.addIssue().
 */
export function createSimultaneousSchema<T extends ZodTypeAny>(
  baseSchema: T,
  validator: (data: z.infer<T>) => ZodValidationIssue[],
): T {
  return baseSchema.superRefine((data, ctx) => {
    const issues = validator(data);
    issues.forEach((issue) =>
      ctx.addIssue({ ...issue, code: z.ZodIssueCode.custom }),
    );
  });
}

export function flattenZodFieldErrorsDeep(
  error: ZodError,
): Record<string, string> {
  return Object.fromEntries(
    error.issues.map((e) => [e.path.join("."), e.message]),
  );
}

export function unflattenErrors(
  flatErrors: Record<string, string>,
): Record<string, any> {
  const result: Record<string, any> = {};

  for (const [path, message] of Object.entries(flatErrors)) {
    const keys = path.split(".");
    let current = result;

    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        current[key] = message;
      } else {
        current[key] = current[key] ?? {};
        current = current[key];
      }
    });
  }

  return result;
}

export function toNullishNumber(val: unknown) {
  if (typeof val === "string") {
    const t = val.trim();
    if (t === "") return null;
    const n = Number(t);
    return isNaN(n) ? val : n;
  }
  return val;
}

export function extractInvalidFields(issues: ZodIssue[]): string[] {
  return [
    ...new Set(
      issues.map((issue) => {
        const [group, ...rest] = issue.path;
        return rest.length > 0
          ? `${String(group)}.${rest.join(".")}`
          : String(group);
      }),
    ),
  ];
}

export const isFilled = (val: unknown) => {
  if (Array.isArray(val)) {
    if (val.length === 0) return false;
    return val.some(
      (v) =>
        (typeof v === "string" && v.trim().length > 0) ||
        typeof v === "number" ||
        typeof v === "boolean",
    );
  }

  if (typeof val === "string") {
    return val.trim().length > 0;
  }

  return val !== null && val !== undefined;
};

export function stripEmptyFields(data: unknown): Record<string, any> {
  // If not an object or completely empty, return an empty object
  if (
    !data ||
    typeof data !== "object" ||
    Array.isArray(data) ||
    !Object.values(data).some((value) => isFilled(value))
  ) {
    return {};
  }

  // Otherwise, keep only the filled values
  return Object.entries(data as Record<string, unknown>).reduce(
    (acc: Record<string, any>, [key, value]) => {
      if (isFilled(value)) {
        acc[key] = value;
      }
      return acc;
    },
    {},
  );
}

export function preprocessToOptionalNumber(val: unknown) {
  if (val === null || val === undefined || val === "") return undefined;
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const parsed = Number(val);
    return isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
}

export const createMaxLengthMessage = (
  translations: Translations,
  maxLength: number,
) => {
  return translations.patientForms.characterLimitValidation({
    numberMost: maxLength.toString(),
  });
};

export const createMinLengthMessage = (
  translations: Translations,
  minLength: number,
) => {
  return translations.patientForms.characterMinValidation({
    numberMinimum: minLength.toString(),
  });
};

export function mapInsuranceAlgoliaHit(
  hit: InsuranceAlgoliaHit,
): InsuranceAlgoliaOption {
  const value = String(hit.id ?? hit.objectID ?? "");
  return {
    careseekerId: hit.careseeker_id,
    id: value,
    is_active: hit.is_active,
    label: hit.name ?? "",
    value,
  };
}

export function mapICDAlgoliaHit(hit: ICDAlgoliaHit): ICDAlgoliaOption {
  const { code, description, objectID } = hit;
  return {
    code,
    description,
    id: objectID ?? code,
    label: `(${code}) ${description}`,
    value: code,
  };
}

/**
 * Shared field for `assessment_variant` across all assessment info step schemas.
 * Preprocesses null → undefined so RHF treats a missing value as unset,
 * then validates against the full ASSESSMENT_VARIANTS enum (optional).
 */
export const auctionAssessmentVariantSchema = z.preprocess(
  (val: unknown) => (val === null ? undefined : val),
  z.enum(ASSESSMENT_VARIANTS).optional(),
);

/**
 * Patient / assessment Zod schemas expect numeric insurer ids (`payers.insurance.id`,
 * `patient.profile.financing.insurance_id`, …) while Algolia options use string keys.
 */
export function insuranceAlgoliaIdToFormNumber(
  raw: string | null | undefined,
): number | null {
  if (raw == null || raw === "") {
    return null;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function toInsuranceAlgoliaOption(
  insurance:
    | { id?: number | string | null; name?: string | null }
    | null
    | undefined,
): InsuranceAlgoliaOption | null {
  if (!insurance) return null;
  const value = insurance.id != null ? String(insurance.id) : "";
  const label = insurance.name ?? "";
  if (!value && !label) return null;
  return {
    id: value || label,
    label,
    value: value || label,
  };
}

export function toIcdCodeAlgoliaOption(
  icdCode:
    | { code?: string | null; description?: string | null }
    | null
    | undefined,
): ICDAlgoliaOption | null {
  if (!icdCode?.code) return null;
  const code = icdCode.code;
  const description = icdCode.description ?? "";
  return {
    code,
    description,
    id: code,
    label: `(${code}) ${description}`,
    value: code,
  };
}

/** Normalise a stored RHF value (legacy select option shape) for insurance pickers. */
export function coerceInsuranceAlgoliaOptionFromFormValue(
  raw: unknown,
): InsuranceAlgoliaOption | null {
  if (raw == null || raw === "") {
    return null;
  }
  if (
    typeof raw === "object" &&
    raw !== null &&
    "label" in raw &&
    "value" in raw
  ) {
    const o = raw as {
      careseekerId?: string;
      careseeker_id?: string;
      id?: unknown;
      is_active?: boolean;
      label: string;
      value: unknown;
    };
    const value = String(o.value ?? o.id ?? "");
    if (!value && !o.label) {
      return null;
    }
    return {
      careseekerId: o.careseekerId ?? o.careseeker_id,
      id: value || o.label,
      is_active: o.is_active,
      label: o.label,
      value: value || o.label,
    };
  }
  return null;
}

/**
 * Assessment forms may send `solutions` as one id, an array, `null`, or omit it.
 * Parsed output is {@link Solutions} (normalized array) or `undefined`.
 */
export const assessmentSolutionsSchema = z
  .union([z.number(), z.array(z.number()), z.null()])
  .optional()
  .transform((val): Solutions | undefined => {
    if (val === undefined || val === null) return undefined;
    return (Array.isArray(val) ? val : [val]) as Solutions;
  });
